import CommonButton from 'components/CommonButton/CommonButton';
import Drawer from 'components/Drawer/Drawer';
import { FC, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import useClinicService from 'hooks/useClinicService';
import { EXAM_PLACEMENT, SERVICE_NAME, SLOT_SHOW_TYPE } from 'utils/constants';
import OptimizeScheduleSection from './OptimizeScheduleSection/OptimizeScheduleSection';
import axiosInstance from 'apis/axiosInstance';
import { loadSelectedClinicId } from 'utils/storage';
import { renderToast } from 'components/Toast/Toast';
import ExamPlacementSection from './ExamPlacementSection/ExamPlacementSection';
import AllowDoctorSelectionSection from './AllowtDoctorSelectionSection/AllowDoctorSelectionSection';
import useClinic from 'hooks/useClinic';
import BlockSchedulingToggleSection from './BlockSchedulingToggleSection/BlockSchedulingToggleSection';
import AvoidBookingWithinTimeFrameSection from './AvoidBookingWithinTimeFrameSection/AvoidBookingWithinTimeFrameSection';

interface AdvancedSettingsProps {
  isShowing: boolean;
  setIsShowing: React.Dispatch<React.SetStateAction<boolean>>;
}

interface FormData {
  examPlacement: string | null;
  slotShowType: string;
  block: string;
  isOptimizeSchedule: boolean;
  optimizeWeeklyDuration: number;
  isDoctorSelectionEnabled: boolean;
  isPreferredPractitionerEnabled: boolean;
  isBlockSchedulingEnabled: boolean;
  futureAppointmentWindowDays: number | null;
}

const formDataDefaultValue = {
  examPlacement: null,
  slotShowType: SLOT_SHOW_TYPE.ANY_TIME_OF_DAY,
  block: '1',
  isOptimizeSchedule: false,
  optimizeWeeklyDuration: 0,
  isDoctorSelectionEnabled: false,
  isPreferredPractitionerEnabled: false,
  isBlockSchedulingEnabled: false,
  futureAppointmentWindowDays: null,
};

const AdvancedSettings: FC<AdvancedSettingsProps> = ({
  isShowing,
  setIsShowing,
}) => {
  const { data: clinic } = useClinic();
  const { data: clinicService, mutate } = useClinicService({
    sort: 'desc,asc',
    sortBy: 'status,order',
  });

  const { control, reset, formState, handleSubmit, setValue, watch } =
    useForm<FormData>({
      mode: 'all',
      defaultValues: formDataDefaultValue,
    });

  const checkIsDuringExamDurationInvalid = (examPlacement?: string | null) => {
    const examCleaning = clinicService?.data.find(
      (service) => service.name === SERVICE_NAME.EXAM_CLEANING
    );

    if (!examCleaning) return;

    const isDuringExamPlacement =
      EXAM_PLACEMENT.DURING_FIRST_PART_DIFF === examPlacement ||
      EXAM_PLACEMENT.DURING_LAST_PART_DIFF === examPlacement;

    if (!isDuringExamPlacement) return;

    const isSomeDoctorDurationGreaterThanHygienistDuration =
      examCleaning.childServices.some((service) =>
        service.isSplitScheduling
          ? service.doctorDuration! > service.hygienistDuration!
          : false
      );

    if (!isSomeDoctorDurationGreaterThanHygienistDuration) return;

    const examPlacementMessage =
      examPlacement === EXAM_PLACEMENT.DURING_FIRST_PART_DIFF
        ? 'During first part of hygiene appt (Different chair)'
        : 'During last part of hygiene appt (Different chair)';

    renderToast({
      type: 'error',
      message: `Settings could not be saved. Please ensure that in all Exam & Cleaning services, the doctor's duration is less than or equal to the hygienist's duration for ${examPlacementMessage} to function properly. Please review and adjust your settings accordingly.`,
    });

    return true;
  };

  const onSubmit = async (data: FormData) => {
    const payload: Partial<FormData> = {
      examPlacement: data.examPlacement,
      isDoctorSelectionEnabled: data.isDoctorSelectionEnabled,
      futureAppointmentWindowDays: data.futureAppointmentWindowDays,
    };

    const isInvalid = checkIsDuringExamDurationInvalid(payload.examPlacement);

    if (isInvalid) return;

    if (data.isOptimizeSchedule) {
      payload.isOptimizeSchedule = data.isOptimizeSchedule;
      payload.slotShowType = data.slotShowType;
      payload.block = data.block;
      payload.optimizeWeeklyDuration = data.optimizeWeeklyDuration;
    }
    const clinicId = loadSelectedClinicId();

    const promises = [];

    if (formState.dirtyFields.isPreferredPractitionerEnabled) {
      promises.push(
        axiosInstance.patch(`/clinics/preferred-practitioner-setting`, {
          isPreferredPractitionerEnabled: data.isPreferredPractitionerEnabled,
        })
      );
    }

    if (
      formState.dirtyFields.examPlacement ||
      formState.dirtyFields.isDoctorSelectionEnabled ||
      formState.dirtyFields.isOptimizeSchedule ||
      formState.dirtyFields.slotShowType ||
      formState.dirtyFields.block ||
      formState.dirtyFields.optimizeWeeklyDuration ||
      formState.dirtyFields.futureAppointmentWindowDays
    ) {
      promises.push(
        axiosInstance.patch(`clinics/${clinicId}/services/settings`, payload)
      );
    }

    try {
      await Promise.all(promises);
      await mutate();
      reset(
        data.isOptimizeSchedule
          ? { ...data }
          : {
              ...formDataDefaultValue,
              examPlacement: data.examPlacement,
              isDoctorSelectionEnabled: data.isDoctorSelectionEnabled,
              isPreferredPractitionerEnabled:
                data.isPreferredPractitionerEnabled,
            }
      );
      setIsShowing(false);
      renderToast({
        type: 'success',
        message: 'Updated settings successfully',
      });
    } catch (error) {
      renderToast({
        type: 'error',
        message: 'Something went wrong. Please try again later',
      });
    }
  };

  const populateDefaultValue = useCallback(() => {
    if (!clinicService || !clinic) return;

    const examCleaningService = clinicService?.data.find(
      (service) => service.name === SERVICE_NAME.EXAM_CLEANING
    );

    if (!examCleaningService) return;

    reset({
      examPlacement: examCleaningService.examPlacement,
      slotShowType:
        examCleaningService.slotShowType || formDataDefaultValue.slotShowType,
      block: examCleaningService.block || formDataDefaultValue.block,
      isOptimizeSchedule:
        examCleaningService.isOptimizeSchedule ||
        formDataDefaultValue.isOptimizeSchedule,
      optimizeWeeklyDuration: examCleaningService.optimizeWeeklyDuration || 0,
      isDoctorSelectionEnabled:
        clinic.isDoctorSelectionEnabled ||
        formDataDefaultValue.isDoctorSelectionEnabled,
      isPreferredPractitionerEnabled:
        clinic.isPreferredPractitionerEnabled ||
        formDataDefaultValue.isPreferredPractitionerEnabled,
      isBlockSchedulingEnabled: clinic.isBlockSchedulingEnabled || false,
      futureAppointmentWindowDays:
        clinic.futureAppointmentWindowDays ??
        formDataDefaultValue.futureAppointmentWindowDays,
    });
  }, [clinic, clinicService, reset]);

  useEffect(() => {
    populateDefaultValue();
  }, [populateDefaultValue]);

  const onReset = () => {
    populateDefaultValue();
  };

  return (
    <Drawer
      title={'Advanced Settings'}
      isShowing={isShowing}
      setIsShowing={setIsShowing}
      onReset={onReset}
      footer={
        <div className="w-full h-6 flex px-2.4 justify-end items-center shadow-primary bottom-0 bg-white sticky">
          <CommonButton
            className="!min-w-[12rem]"
            disabled={!formState.isValid || !formState.isDirty}
            onClick={handleSubmit(onSubmit)}
            isLoading={formState.isSubmitting}
          >
            Save
          </CommonButton>
        </div>
      }
    >
      <div className="flex flex-col gap-y-2.8 -mx-0.8">
        <BlockSchedulingToggleSection control={control} />
        <AvoidBookingWithinTimeFrameSection
          control={control}
          setValue={setValue}
        />
        <ExamPlacementSection control={control} />
        <AllowDoctorSelectionSection
          control={control}
          setValue={setValue}
          isPreferredPractitionerEnabled={watch(
            'isPreferredPractitionerEnabled'
          )}
        />
        <OptimizeScheduleSection control={control} />
      </div>
    </Drawer>
  );
};

export default AdvancedSettings;
